import { Controller } from "@hotwired/stimulus"

// Draws one dial's trend with Chart.js.
//
// A Stimulus controller rather than the nonced inline <script> the older
// dashboards use (admin/scoreboard, countries): those need a data-rendered
// guard and a setTimeout poll for window.Chart precisely because an inline
// script re-runs unpredictably across Turbo visits. Connecting and
// disconnecting is the lifecycle we actually want, and destroy() on disconnect
// is what stops a Turbo visit leaking a canvas' worth of listeners each time.
export default class extends Controller {
  static values = {
    labels: Array,
    series: Array,
    unit: { type: String, default: "count" },
    kind: { type: String, default: "line" },
    label: { type: String, default: "" }
  }

  connect() {
    this.render()
    // Redraw on theme flips so grid/tick colours follow light<->dark. Chart.js
    // bakes the colours in at construction, so there is nothing to update in
    // place — the chart has to be rebuilt.
    this.themeObserver = new MutationObserver(() => this.render())
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bs-theme"]
    })
  }

  disconnect() {
    this.themeObserver?.disconnect()
    this.chart?.destroy()
    this.chart = null
  }

  render() {
    if (!window.Chart) return
    this.chart?.destroy()

    const dark = document.documentElement.getAttribute("data-bs-theme") === "dark"
    const tick = dark ? "rgba(148, 163, 184, 0.8)" : "rgba(15, 23, 42, 0.6)"
    const grid = dark ? "rgba(148, 163, 184, 0.14)" : "rgba(15, 23, 42, 0.08)"
    const accent = "#2563eb"

    this.chart = new window.Chart(this.element, {
      type: this.kindValue,
      data: {
        labels: this.labelsValue,
        datasets: [
          {
            label: this.labelValue,
            data: this.seriesValue,
            borderColor: accent,
            backgroundColor: this.kindValue === "bar" ? accent : "rgba(37, 99, 235, 0.08)",
            borderRadius: this.kindValue === "bar" ? 4 : 0,
            fill: this.kindValue !== "bar",
            tension: 0.3,
            pointRadius: 0,
            pointHitRadius: 12
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => this.format(context.parsed.y)
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              color: tick,
              // Counts are whole things; money and rates are not.
              precision: this.unitValue === "count" ? 0 : undefined,
              callback: (value) => this.format(value, true)
            },
            grid: { color: grid }
          },
          x: {
            ticks: { color: tick, maxTicksLimit: 8, autoSkip: true },
            grid: { display: false }
          }
        }
      }
    })
  }

  // Mirrors DialsHelper#dial_value so an axis tick and a tooltip read the same
  // way as every server-rendered figure on the page. Kept deliberately small:
  // the table beside the chart is the full-precision surface.
  format(value, compact = false) {
    if (value === null || value === undefined) return "—"

    switch (this.unitValue) {
      case "usd":
        return `$${this.compact(value, compact)}`
      case "percent":
        return `${this.trim(value, 1)}%`
      case "seconds":
        return this.duration(value)
      case "ratio":
        return this.trim(value, 2)
      default:
        return this.compact(value, compact)
    }
  }

  compact(value, force = false) {
    const abs = Math.abs(value)
    if (!force && abs < 1000) return this.trim(value, abs < 10 ? 2 : 0)
    if (abs < 1000) return this.trim(value, abs < 10 ? 2 : 0)
    const units = [
      [1e9, "B"],
      [1e6, "M"],
      [1e3, "K"]
    ]
    const found = units.find(([threshold]) => abs >= threshold)
    if (!found) return this.trim(value, 0)
    const scaled = value / found[0]
    return `${this.trim(scaled, Math.abs(scaled) < 10 ? 1 : 0)}${found[1]}`
  }

  duration(seconds) {
    if (seconds < 1) return "0s"
    const mins = Math.floor(seconds / 60)
    const secs = Math.round(seconds % 60)
    if (mins === 0) return `${secs}s`
    const hours = Math.floor(mins / 60)
    if (hours === 0) return `${mins}m ${secs}s`
    return `${hours}h ${mins % 60}m`
  }

  // Mirrors DialsHelper#dial_number, including WHY the zero-stripping is
  // anchored to the fractional part: a bare /\.?0+$/ also strips significant
  // zeros from the integer part, which rendered the y-axis tick for 10 as "1".
  trim(value, decimals) {
    const fixed = Number(value).toFixed(decimals)
    const trimmed = fixed.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "")
    return trimmed === "" || trimmed === "-" ? "0" : trimmed
  }
}
